import { compact } from '@lglp/utils';
import { AlgoliaSearchOptions } from 'algoliasearch';
import { keys, length, map, mergeDeepRight, omit } from 'ramda';

import { getClient, getAlgoliaIndexName } from './algolia-api';
import { normalizeFacets } from './normalize';

const generateFacetFilters = (
  facets: Record<string, string[]> = {}
): string[][] | void => {
  const result = Object.entries(facets).reduce<string[][]>(
    (acc, [filter, values]) => {
      if (values.length) {
        return [...acc, values.map((value) => `filters.${filter}:${value}`)];
      }
      return acc;
    },
    []
  );

  return length(result) ? result : undefined;
};

export enum SortBy {
  NONE = 'none',
  DEFAULT = 'default',
  CREATED_AT = 'created_at',
  UPDATED_AT = 'updated_at',
  PUBLISHED_AT = 'published_at',
}

const getLocalizedIndexedClient = (lang: string, sortBy: SortBy): string => {
  let suffix = '';
  switch (sortBy) {
    case SortBy.DEFAULT:
    case SortBy.CREATED_AT:
      suffix = '_sorted';
      break;
    case SortBy.UPDATED_AT:
      suffix = '_sorted_byupdate';
      break;
    case SortBy.PUBLISHED_AT:
      suffix = '_sorted_bypublication';
      break;
    case SortBy.NONE:
    default:
      break;
  }
  return `${getAlgoliaIndexName(lang)}${suffix}`;
};

type SearchResults = {
  query?: string;
  count: number;
  page: number;
  total_page: number;
  results: any[];
  facets: Record<string, SearchFacet>;
};

type SearchFacet = {
  nbResults: number;
  values: Record<string, number>;
};

type SearchConfig = Omit<
  AlgoliaSearchOptions,
  'query' | 'facetFilters' | 'hitsPerPage' | 'page'
> & {
  query?: string;
  lang: string;
  sortBy?: SortBy;
  facetFilters?: Record<string, string[]>;
  hitsPerPage?: number;
  queryWithoutExcludedFilters?: Record<string, any>;
  page?: number;
};

export const search = async ({
  query,
  lang,
  sortBy = SortBy.DEFAULT,
  facetFilters,
  hitsPerPage = 0,
  page = 1,
  queryWithoutExcludedFilters = [],
  ...options
}: SearchConfig): Promise<SearchResults> => {
  const indexName = getLocalizedIndexedClient(lang, sortBy);

  const commonOptions = {
    ...options,
    hitsPerPage: hitsPerPage > 0 ? hitsPerPage : undefined,
    // Algolia start page index to 0. We choose an unified pagination system.
    // We start page index to 1
    page: page - 1,
  };

  const { results } = await getClient().search(
    [
      {
        indexName,
        query,
        params: compact({
          ...commonOptions,
          facetFilters: generateFacetFilters(facetFilters),
        }),
      },

      ...[
        queryWithoutExcludedFilters
          ? {
              indexName,
              query,
              params: compact({
                ...mergeDeepRight(commonOptions, queryWithoutExcludedFilters),
                facetFilters: generateFacetFilters(facetFilters),
              }),
            }
          : undefined,
      ],

      ...map(
        (filter) => ({
          indexName,
          query,
          params: compact({
            ...commonOptions,
            facets: `filters.${filter as string}`,
            facetFilters: generateFacetFilters(
              omit([filter as string], facetFilters)
            ),
          }),
        }),
        keys(compact(facetFilters || {}))
      ),
    ].filter(Boolean)
  );

  return {
    query: results?.[0]?.query,
    count: results?.[0]?.nbHits || 0,
    page: results?.[0]?.page + 1,
    total_page: results?.[0]?.nbPages || 0,
    results: results?.[0]?.hits,
    facets: normalizeFacets(results),
  };
};
